/**
 * AppLayout — ShellLayout thin wrapper.
 *
 * 도메인 패널 + breadcrumb + 모바일 Sheet.
 * 공통 셸(ShellLayout) + 프리미티브(PanelToggleButton, usePersistentState)로 구성.
 */
import { Link, useLocation } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  AppBreadcrumb,
  PanelToggleButton,
  usePersistentState,
} from "@/shared/components";
import { stripModePrefix, toModePath } from "@/app/mode/config";
import {
  NavPanelContent,
  MobileNavSheet,
  getSectionTrail,
  isSectionActive,
  type SidebarSection,
} from "@/shared/components/sidebar-navigation";
import {
  DOMAIN_META,
  type DomainKey,
  type NavItem,
} from "@/shared/lib/tool-registry";
import { ShellLayout } from "@/shared/layouts/shell-layout";
import { SidePanel } from "@/shared/layouts/side-panel";
import {
  RAIL_HELP,
  RAIL_MYPAGE,
  RAIL_DOMAINS,
  ALL_RAIL_ITEMS,
  domainSections as _domainSections,
} from "@/shared/layouts/rail-items";

/* ─── Section data ─── */

const domainSections: SidebarSection[] = _domainSections.map((section) => ({
  key: section.domain,
  title: section.meta.label,
  icon: DOMAIN_META[section.domain as DomainKey]?.icon,
  items: section.items,
}));

const domainIds = new Set(RAIL_DOMAINS.map((d) => d.id));

/* ─── Helpers ─── */

function getDefaultGroupOpen(
  item: NavItem,
  currentPath: string,
  manualGroups: Record<string, boolean>,
) {
  const manual = manualGroups[item.label];
  if (manual !== undefined) return manual;
  return isSectionActive([item], currentPath);
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [activeDomain, setActiveDomain] = usePersistentState<string | null>(
    "datalab_nav_panel_domain",
    null,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [manualGroups, setManualGroups] = useState<Record<string, boolean>>({});

  const location = useLocation();
  const currentPath = location.pathname;
  const activePath = stripModePrefix(currentPath);

  const sections = useMemo(
    () =>
      domainSections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          to: toModePath(item.to, currentPath),
          children: item.children?.map((child) => ({
            ...child,
            to: toModePath(child.to, currentPath),
          })),
        })),
      })),
    [currentPath],
  );

  const activeRailId = useMemo(() => {
    for (const item of ALL_RAIL_ITEMS) {
      if (
        item.to &&
        (activePath === item.to || activePath.startsWith(item.to + "/"))
      ) {
        return item.id;
      }
    }
    const section = domainSections.find((s) =>
      isSectionActive(s.items, activePath),
    );
    return section?.key ?? null;
  }, [activePath]);

  const activeSection = useMemo(
    () => domainSections.find((s) => isSectionActive(s.items, activePath)),
    [activePath],
  );
  const activeTrail = activeSection
    ? getSectionTrail(activeSection, activePath)
    : [];

  const toggleGroup = useCallback((key: string, open: boolean) => {
    setManualGroups((prev) => ({ ...prev, [key]: !open }));
  }, []);

  const isGroupOpen = useCallback(
    (item: NavItem) => getDefaultGroupOpen(item, activePath, manualGroups),
    [activePath, manualGroups],
  );

  function handleRailClick(id: string) {
    if (domainIds.has(id)) {
      setActiveDomain((prev: any) => (prev === id ? null : id));
    }
  }

  const panelSection = sections.find((s) => s.key === activeDomain);
  const showPanelToggle = activeRailId && domainIds.has(activeRailId);
  const helpTo = "/docs";
  const mypageTo = toModePath("/member/mypage", currentPath);

  return (
    <ShellLayout
      railActiveItem={activeRailId ?? activeDomain ?? undefined}
      onRailItemClick={handleRailClick}
      sidePanel={
        panelSection ? (
          <SidePanel title={panelSection.title} icon={panelSection.icon}>
            <NavPanelContent
              section={panelSection}
              currentPath={activePath}
              isGroupOpen={isGroupOpen}
              toggleGroup={toggleGroup}
            />
          </SidePanel>
        ) : undefined
      }
      header={
        <>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-1 text-text-tertiary hover:bg-surface-secondary sm:hidden"
          >
            <Menu className="size-5" />
          </button>
          {/* Panel toggle */}
          {showPanelToggle && (
            <PanelToggleButton
              isOpen={!!panelSection}
              onToggle={() =>
                setActiveDomain((prev: any) => (prev ? null : activeRailId))
              }
              className="hidden md:flex"
            />
          )}
          {/* Breadcrumb */}
          <div className="min-w-0 flex-1">
            <AppBreadcrumb segments={activeTrail} />
          </div>
          {/* Mobile-only icons */}
          <div className="flex items-center gap-1 sm:hidden">
            <Link
              to={helpTo}
              className="flex size-8 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-surface-secondary hover:text-text-primary"
              title="도움말"
            >
              {RAIL_HELP.icon}
            </Link>
            <Link
              to={mypageTo}
              className="flex size-8 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-surface-secondary hover:text-text-primary"
              title="마이페이지"
            >
              {RAIL_MYPAGE.icon}
            </Link>
          </div>
        </>
      }
      overlays={
        <MobileNavSheet
          open={mobileOpen}
          onOpenChange={setMobileOpen}
          sections={sections}
          currentPath={activePath}
          isGroupOpen={isGroupOpen}
          toggleGroup={toggleGroup}
        />
      }
    >
      <div
        className="flex flex-1 min-h-0 flex-col overflow-y-auto"
        key={currentPath}
      >
        {children}
      </div>
    </ShellLayout>
  );
}
